import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { addBookingToSteps } from "@/lib/liSequences";

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const ready = (p) => p.steps?.length === 3 && p.steps.every((s) => s.subject && s.body);

// Fallback when start-batch isn't live: approve every prospect whose 3 emails are written.
const approveEach = async (batch, audience) => {
  const { data } = await api.get(`/admin/rcc/li/prospects?audience=${audience}&limit=1000`);
  const mine = (data?.items || []).filter((p) => (batch.ids?.length ? batch.ids.includes(p.id) : batch.emails.includes((p.email || "").toLowerCase())) && (p.status || "draft") === "draft");
  let started = 0;
  for (const p of mine.filter(ready)) { try { await api.post(`/admin/rcc/li/prospects/${p.id}/approve`, { steps: addBookingToSteps(p.steps, audience, p.id), tz: tz() }); started++; } catch { /* left as draft */ } }
  return { started, pending: mine.length - started, fallback: true };
};

export const StartBatch = ({ batch, audience, onDone, onClose }) => {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = batch.ids?.length || batch.emails.length;
  const start = async () => {
    setBusy(true);
    try {
      let r;
      try { r = (await api.post("/admin/rcc/li/prospects/start-batch", { ids: batch.ids || [], emails: batch.ids?.length ? [] : batch.emails, audience, tz: tz(), auto_start_pending: true })).data; }
      catch (e) { if (e.response?.status !== 404) throw e; r = await approveEach(batch, audience); }
      const pend = r.pending || 0;
      toast.success(`${r.started || 0} sequence${r.started === 1 ? "" : "s"} started.${pend ? (r.fallback ? ` ${pend} still being written: start them from the list when ready (auto-start isn't live yet, spec §59).` : ` ${pend} will start automatically as soon as their emails are written.`) : ""}`);
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setAsk(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-3" data-testid="li-start-batch">
      <p className="min-w-0 flex-1 basis-[calc(100%-2rem)] text-sm sm:basis-auto text-zinc-100" data-testid="li-start-batch-text">{n} prospect{n === 1 ? "" : "s"} created with drafted sequences. Start them all now?</p>
      <Button size="sm" onClick={() => setAsk(true)} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="li-start-batch-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}Start {n === 1 ? "1 sequence" : `all ${n} sequences`}</Button>
      <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="li-start-batch-dismiss"><X className="h-4 w-4" /></button>
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent className="border-[#27272A] bg-[#0f0f12]" data-testid="li-start-batch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Start {n} sequence{n === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Real emails will go out within your send window: email 1 first, then day 3 and day 7 unless they reply. Sequences the AI is still writing start automatically as soon as they're ready. You can stop any of them from the list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="li-start-batch-cancel">Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); start(); }} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="li-start-batch-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Start {n}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
